import * as THREE from 'three';
import {
  ANGLE_ROTATIONS,
  CHARACTER_MODEL,
  TEXTURE_NAME,
  TEXTURE_EXT,
  type CameraAngle,
} from '@/config/exercise3d';
import { disposeObject3D, loadAnimationClip, loadGLBScene, loadTexture } from './loaders';

const CROSSFADE_SECONDS = 0.3;

type Status = 'loading' | 'ready' | 'error';

export interface SceneEvents {
  onStatus?: (status: Status, error?: string) => void;
  onAnimationLoop?: () => void;
}

export class SceneController {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private pivot: THREE.Group | null = null;
  private model: THREE.Group | null = null;
  private mixer: THREE.AnimationMixer | null = null;
  private clock = new THREE.Clock();
  private clips = new Map<string, THREE.AnimationClip>();
  private currentAction: THREE.AnimationAction | null = null;
  private animFrame = 0;
  private targetAngle = 0;
  private paused = false;
  private playbackSpeed = 1.0;
  private destroyed = false;
  private resizeObs: ResizeObserver | null = null;
  private ready = false;

  constructor(
    private canvas: HTMLCanvasElement,
    private events: SceneEvents = {},
  ) {}

  async init(initialModelName: string, initialAngle: CameraAngle = 'front'): Promise<void> {
    this.events.onStatus?.('loading');
    this.targetAngle = ANGLE_ROTATIONS[initialAngle] ?? 0;

    try {
      const renderer = new THREE.WebGLRenderer({
        canvas: this.canvas,
        antialias: true,
        powerPreference: 'high-performance',
      });
      renderer.setClearColor(0x0d0d0f);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer = renderer;

      const scene = new THREE.Scene();
      scene.fog = new THREE.Fog(0x0d0d0f, 500, 800);
      this.scene = scene;

      const camera = new THREE.PerspectiveCamera(30, 1, 1, 2000);
      this.camera = camera;

      scene.add(new THREE.AmbientLight(0xffffff, 0.6));
      const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
      keyLight.position.set(100, 300, 200);
      scene.add(keyLight);
      const rimLight = new THREE.DirectionalLight(0xff6b35, 0.3);
      rimLight.position.set(-100, 100, -100);
      scene.add(rimLight);

      const pivot = new THREE.Group();
      scene.add(pivot);
      this.pivot = pivot;

      const model = await loadGLBScene(CHARACTER_MODEL);
      if (this.destroyed) {
        disposeObject3D(model);
        return;
      }

      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      model.position.x = -center.x;
      model.position.z = -center.z;
      model.position.y = -box.min.y;

      model.traverse((child: any) => {
        if (child.isMesh) {
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          mats.forEach((mat: any) => {
            if (!mat.color || mat.color.getHex() === 0x000000) {
              mat.color = new THREE.Color(0xaaaaaa);
            }
            mat.metalness = 0.1;
            mat.roughness = 0.8;
          });
        }
      });

      pivot.add(model);
      this.model = model;

      loadTexture(TEXTURE_NAME, TEXTURE_EXT).then((texture) => {
        if (!texture) return;
        if (this.destroyed || !this.model) {
          texture.dispose();
          return;
        }
        this.model.traverse((child: any) => {
          if (child.isMesh) {
            const mats = Array.isArray(child.material) ? child.material : [child.material];
            mats.forEach((mat: any) => {
              mat.map = texture;
              mat.needsUpdate = true;
            });
          }
        });
      });

      const finalBox = new THREE.Box3().setFromObject(model);
      const modelHeight = finalBox.max.y - finalBox.min.y;
      camera.position.set(0, modelHeight * 0.65, modelHeight * 4);
      camera.lookAt(0, modelHeight * 1.1, 0);

      const mixer = new THREE.AnimationMixer(model);
      mixer.addEventListener('loop', (e: any) => {
        if (e.action === this.currentAction) this.events.onAnimationLoop?.();
      });
      this.mixer = mixer;

      if (model.animations?.length) {
        this.clips.set('default', model.animations[0]);
      }

      await this.loadAndPlay(initialModelName);

      this.resizeToCanvas();
      this.resizeObs = new ResizeObserver(() => this.resizeToCanvas());
      this.resizeObs.observe(this.canvas);

      this.ready = true;
      this.events.onStatus?.('ready');
      this.startRenderLoop();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.events.onStatus?.('error', msg);
      throw err;
    }
  }

  private async loadAndPlay(modelName: string): Promise<void> {
    if (!this.mixer) return;
    try {
      if (!this.clips.has(modelName)) {
        const clip = await loadAnimationClip(modelName);
        if (!clip || this.destroyed) return;
        this.clips.set(modelName, clip);
      }
      const clip = this.clips.get(modelName);
      if (!clip) return;
      if (this.currentAction) this.currentAction.fadeOut(CROSSFADE_SECONDS);
      const action = this.mixer.clipAction(clip);
      action.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(CROSSFADE_SECONDS).play();
      this.currentAction = action;
    } catch {
      const fallback = this.clips.get('default');
      if (fallback && this.mixer) {
        const action = this.mixer.clipAction(fallback);
        action.reset().setLoop(THREE.LoopRepeat, Infinity).play();
        this.currentAction = action;
      }
    }
  }

  private startRenderLoop(): void {
    const tick = () => {
      if (this.destroyed) return;
      this.animFrame = requestAnimationFrame(tick);

      if (!this.paused) {
        const delta = this.clock.getDelta();
        this.mixer?.update(delta * this.playbackSpeed);
      } else {
        this.clock.getDelta();
      }

      if (this.pivot) this.pivot.rotation.y = this.targetAngle;

      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    };
    tick();
  }

  private resizeToCanvas(): void {
    if (!this.renderer || !this.camera) return;
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    if (width === 0 || height === 0) return;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  async setExercise(modelName: string): Promise<void> {
    if (!this.ready) return;
    await this.loadAndPlay(modelName);
  }

  setRotation(angleRadians: number): void {
    this.targetAngle = angleRadians;
  }

  setCameraAngle(angle: CameraAngle): void {
    this.targetAngle = ANGLE_ROTATIONS[angle] ?? 0;
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
  }

  setPlaybackSpeed(speed: number): void {
    this.playbackSpeed = speed;
  }

  isReady(): boolean {
    return this.ready;
  }

  dispose(): void {
    this.destroyed = true;
    cancelAnimationFrame(this.animFrame);
    this.resizeObs?.disconnect();
    this.currentAction?.stop();
    this.mixer?.stopAllAction();
    this.mixer = null;
    if (this.model) disposeObject3D(this.model);
    this.model = null;
    this.pivot = null;
    this.scene?.clear();
    this.scene = null;
    this.camera = null;
    this.clips.clear();
    this.renderer?.dispose();
    this.renderer = null;
  }
}
